<template>
    <VJoyPanel class="quote-summary">
        <template #panel-title>{{ $t('projectclient.quote.view.quote-summary.title') }}</template>

        <template #panel-subtitle>
            {{
                $t('projectclient.quote.view.quote-summary.subtitle', {
                    0: escapeHtml(formatDate(quote.submissionDate)),
                    1: escapeHtml(formatDate(quote.expirationDate)),
                })
            }}
        </template>

        <template #panel-body>
            <!-- PURCHASE ORDER SECTION -->
            <VJoyWrapper v-if="!isPunchoutEnabled">
                <div class="quote-summary__po-section" data-product-tour-purchase-order>
                    <VJoyInput
                        ref="purchaseOrderInputRef"
                        v-model="purchaseOrder"
                        :placeholder="$t('projectclient.quote.view.quote-summary.input.purchase-order.placeholder')"
                        name="purchase-order"
                        size="small"
                        @focus="activateSave"
                    />
                    <VJoyButton
                        variant="main"
                        size="small"
                        :disabled="isInputDisabled"
                        data-testid="save-purchase-order"
                        @click="handleSetPurchaseOrder"
                    >
                        {{ $t('general.save') }}
                    </VJoyButton>
                </div>
                <ValidationNotification
                    v-if="isRegexFaulty && purchaseOrder"
                    :label="$t('projectclient.quote.view.quote-summary.dialog.purchase-order.format')"
                    type="neutral"
                />
            </VJoyWrapper>

            <!-- AMOUNT SUMMARY TIME-BASED -->
            <div v-if="isQuoteTimeAndMaterials(quote)" class="quote-summary__amounts">
                <template v-if="isFeesInfoVisible">
                    <div class="quote-summary__price-details__wrapper">
                        <p>{{ $t(`projectclient.quote.view.quote-summary.price-details.time-based.label.rate.${quote.details.workRate.unit}`) }}</p>
                        <p class="price-details__value">
                            {{ handleCurrency(quote.details.workRate.value, quote.currencyCode, locale) }}
                        </p>
                    </div>

                    <div class="quote-summary__price-details__wrapper">
                        <p>{{ $t('projectclient.quote.view.quote-summary.price-details.time-based.label.service-fees') }}</p>
                        <p class="price-details__value">
                            {{ handleCurrency(feesPerWorkUnitForClient, quote.currencyCode, locale) }}
                        </p>
                    </div>
                    <hr />
                </template>

                <div class="quote-summary__price-details__wrapper">
                    <VJoyTooltip>
                        <VJoyWrapper align="center" no-margin column-gap="1">
                            <p class="bold">
                                {{ $t(`projectclient.quote.view.quote-summary.price-details.time-based.label.cost.${quote.details.workRate.unit}`) }}
                            </p>
                            <VJoyIcon name="question-circle" role="button" color="neutral" />
                        </VJoyWrapper>

                        <template #tooltip-content>
                            <p>
                                {{
                                    $t(`projectclient.quote.view.quote-summary.price-details.time-based.tooltip.cost.${quote.details.workRate.unit}`)
                                }}
                            </p>
                        </template>
                    </VJoyTooltip>

                    <p class="bold">
                        {{ handleCurrency(workRateForClient, quote.currencyCode, locale) }}
                    </p>
                </div>
                <VJoyHighlight class="background">
                    <div class="quote-summary__price-details__wrapper">
                        <VJoyTooltip>
                            <div class="quote-summary__price-details__wrapper__tooltip-label">
                                <p class="bold teal">
                                    {{ $t('projectclient.quote.view.quote-summary.price-details.time-based.label.estimated-total') }}
                                </p>
                                <VJoyIcon name="question-circle" role="button" color="neutral" />
                            </div>
                            <template #tooltip-content>
                                {{ $t('projectclient.quote.view.quote-summary.price-details.time-based.tooltip.estimated-total') }}
                            </template>
                        </VJoyTooltip>
                        <p class="bold teal">{{ handleCurrency(quote.amounts.totalAmountExcludingTaxes, quote.currencyCode, locale) }}</p>
                    </div>

                    <p class="joy-mt-2 small">
                        <template v-if="isPaymentDetailsEnabled">
                            {{
                                $t(
                                    `projectclient.quote.view.quote-summary.price-details.time-based.description.estimated-total-reassuring.${quote.details.workRate.unit}`,
                                    {workRate: quote.details.estimatedTotalWorkAmount},
                                )
                            }}
                        </template>
                        <template v-else>
                            {{
                                $t(
                                    `projectclient.quote.view.quote-summary.price-details.time-based.description.estimated-total.${quote.details.workRate.unit}`,
                                    {workRate: quote.details.estimatedTotalWorkAmount},
                                )
                            }}
                        </template>
                    </p>
                </VJoyHighlight>
            </div>

            <!-- AMOUNT SUMMARY FIXED-PRICE -->
            <div v-else-if="isQuoteFixedPrice(quote)" class="quote-summary__amounts">
                <div v-if="isFeesInfoVisible" class="quote-summary__price-details__wrapper">
                    <p>{{ $t('projectclient.quote.view.quote-summary.price-details.fixed-price.label.sub-total') }}</p>
                    <p class="price-details__value">
                        {{ handleCurrency(quote.amounts.serviceAmountExcludingTaxes, quote.currencyCode, locale) }}
                    </p>
                </div>

                <div v-if="isFeesInfoVisible" class="quote-summary__price-details__wrapper">
                    <VJoyTooltip>
                        <VJoyWrapper align="center" no-margin column-gap="1">
                            {{ $t('projectclient.quote.view.quote-summary.price-details.fixed-price.label.service-fees') }}
                            <VJoyIcon name="question-circle" role="button" color="neutral" />
                        </VJoyWrapper>
                        <template #tooltip-content>
                            <p>{{ $t('projectclient.quote.view.quote-summary.price-details.fixed-price.tooltip.service-fees') }}</p>
                        </template>
                    </VJoyTooltip>

                    <p>{{ handleCurrency(quote.amounts.feesAmountExcludingTaxes, quote.currencyCode, locale) }}</p>
                </div>

                <VJoyHighlight class="background">
                    <div class="quote-summary__price-details__wrapper">
                        <div>
                            <p class="bold teal">
                                {{ $t('projectclient.quote.view.quote-summary.price-details.fixed-price.label.total-excluding-tax') }}
                            </p>
                            <p class="small">
                                {{ $t('projectclient.quote.view.quote-summary.price-details.fixed-price.description.total-excluding-tax') }}
                            </p>
                        </div>

                        <p class="price-details__value bold teal">
                            {{ handleCurrency(quote.amounts.totalAmountExcludingTaxes, quote.currencyCode, locale) }}
                        </p>
                    </div>
                </VJoyHighlight>

                <div class="quote-summary__price-details__wrapper">
                    <VJoyTooltip>
                        <VJoyWrapper align="center" no-margin column-gap="1">
                            {{ $t('projectclient.quote.view.quote-summary.price-details.fixed-price.label.taxes') }}
                            <VJoyIcon name="question-circle" role="button" color="neutral" />
                        </VJoyWrapper>

                        <template #tooltip-content>
                            <p>{{ $t('projectclient.quote.view.quote-summary.price-details.fixed-price.tooltip.taxes') }}</p>
                        </template>
                    </VJoyTooltip>

                    <p>
                        {{
                            handleCurrency(
                                quote.amounts.totalAmountIncludingTaxes - quote.amounts.totalAmountExcludingTaxes,
                                quote.currencyCode,
                                locale,
                            )
                        }}
                    </p>
                </div>
                <div class="quote-summary__price-details__wrapper">
                    <p class="bold teal">{{ $t('projectclient.quote.view.quote-summary.price-details.fixed-price.total-including-tax') }}</p>
                    <p class="price-details__value bold teal">
                        {{ handleCurrency(quote.amounts.totalAmountIncludingTaxes, quote.currencyCode, locale) }}
                    </p>
                </div>
            </div>
        </template>

        <template v-if="isQuoteActionable(quote)" #panel-action>
            <div class="quote-summary__quote-actions">
                <div v-if="isPunchoutEnabled" class="quote-summary__cta-group">
                    <!-- PUNCHOUT VALIDATION WORKFLOW -->
                    <template v-if="punchoutStatus === 'WAITING_TO_BE_SENT'">
                        <VJoyButton variant="main" :loading="loading" data-testid="forward-quote-to-erp" @click="handlePunchout">
                            {{ $t('projectclient.quote.view.quote-summary.actions.forward-to-erp', {0: erpName}) }}
                        </VJoyButton>

                        <VJoyButton
                            variant="secondary"
                            :disabled="loading"
                            data-testid="suggest-quote-modification"
                            @click="displayFreelancerMessengerDrawer"
                        >
                            {{ $t('projectclient.quote.view.quote-summary.actions.suggest-modification') }}
                        </VJoyButton>

                        <VJoyButton variant="ghost" :disabled="loading" data-testid="decline-quote" @click="openDeclineQuoteDialog">
                            {{ $t('projectclient.quote.view.quote-summary.actions.decline-quote') }}
                        </VJoyButton>
                    </template>

                    <template v-else-if="punchoutStatus === 'SENT_FOR_VALIDATION'">
                        <VJoyHighlight class="joy-mt-4" level="info" display-icon icon="info-circle">
                            <p>{{ $t('projectclient.quote.view.quote-summary.dialog.punchout.highlight.after-confirm', {0: erpName}) }}</p>
                        </VJoyHighlight>
                    </template>
                </div>

                <div v-else-if="quote.options.needPreAcceptation" class="quote-summary__cta-group">
                    <!-- PRE-VALIDATION WORKFLOW-->
                    <VJoyHighlight class="quote-summary__quote-actions__highlight" level="warning" display-icon icon="warning-triangle">
                        <p>
                            {{ $t('projectclient.quote.view.quote-summary.dialog.pre-accept.highlight.before-confirm') }}
                        </p>
                    </VJoyHighlight>

                    <VJoyButton variant="main" data-testid="pre-accept-quote" @click="preAcceptRequirementCheck">
                        {{ $t('projectclient.quote.view.quote-summary.actions.pre-accept-quote') }}
                    </VJoyButton>

                    <VJoyButton
                        variant="secondary"
                        :disabled="loading"
                        data-testid="suggest-quote-modification"
                        @click="displayFreelancerMessengerDrawer"
                    >
                        {{ $t('projectclient.quote.view.quote-summary.actions.suggest-modification') }}
                    </VJoyButton>

                    <VJoyButton variant="ghost" :disabled="loading" data-testid="decline-quote" @click="openDeclineQuoteDialog">
                        {{ $t('projectclient.quote.view.quote-summary.actions.decline-quote') }}
                    </VJoyButton>
                </div>

                <div v-else class="quote-summary__cta-group">
                    <!-- NOMINAL VALIDATION WORKFLOW-->
                    <VJoyHighlight
                        v-if="isQuoteAcceptanceBlockedFor(quote, QuoteAcceptanceBlockingReason.AdministratorValidationRequired)"
                        class="quote-summary__quote-actions__highlight"
                        level="info"
                        display-icon
                        icon="info-circle"
                        data-testid="quote-pre-accept-after-confirm-highlight"
                    >
                        <p>
                            {{ $t('projectclient.quote.view.quote-summary.dialog.pre-accept.highlight.after-confirm') }}
                        </p>
                    </VJoyHighlight>

                    <VJoyTooltip :text="acceptQuoteTooltipText" class="quote-summary__accept-quote-tooltip" data-testid="accept-quote-tooltip">
                        <span class="quote-summary__accept-quote-tooltip__trigger" data-testid="accept-quote-tooltip-trigger">
                            <VJoyButton
                                variant="main"
                                full
                                :disabled="!!quote.options.acceptanceBlockingReason || loading"
                                :loading="loading"
                                data-testid="accept-quote"
                                @click="acceptRequirementCheck"
                            >
                                {{ $t('projectclient.quote.view.quote-summary.actions.accept-quote') }}
                            </VJoyButton>
                        </span>
                    </VJoyTooltip>

                    <VJoyButton
                        variant="secondary"
                        :disabled="loading"
                        data-testid="suggest-quote-modification"
                        @click="displayFreelancerMessengerDrawer"
                    >
                        {{ $t('projectclient.quote.view.quote-summary.actions.suggest-modification') }}
                    </VJoyButton>

                    <VJoyButton variant="ghost" :disabled="loading" data-testid="decline-quote" @click="openDeclineQuoteDialog">
                        {{ $t('projectclient.quote.view.quote-summary.actions.decline-quote') }}
                    </VJoyButton>
                </div>
            </div>
        </template>
    </VJoyPanel>
    <PurchaseOrderProductTour v-if="showPurchaseOrderTour" @add-later="onPurchaseOrderTourAddLater" @add-now="onPurchaseOrderTourAddNow" />
</template>

<script setup lang="ts">
    import {computed, nextTick, ref, useTemplateRef, watch, type Ref} from 'vue';
    import {useDialog, useTrackPurchaseOrder} from '@common/composables';
    import {eventBus} from '@common/events';
    import {useUserStore} from '@common/stores';
    import {FreelancerDrawerTab, ValidationNotification} from '@common/ui';
    import {handleCurrency} from '@common/utils';
    import {useClientQuoteViewStore} from '@features/quote';
    import {storeToRefs} from 'pinia';
    import {useAmplitudeSessionReplay, useDataTracking, useFormat, useLocale, useTranslation} from '#imports';
    import {useFeatureFlag} from '@malt/feature-flags';
    import {validateForm} from '@malt/forms';
    import {escapeHtml} from '@malt/sanitize';
    import {useEventBus} from '@malt/utils';
    import {VJoyButton, VJoyHighlight, VJoyIcon, VJoyInput, VJoyPanel, VJoyTooltip, VJoyWrapper} from '@maltjoy/core-vue';
    import {InvoicingMode, QuoteAcceptanceBlockingReason, type QuoteResource} from '@api';
    import {
        AddPhoneNumberModal,
        DeclineQuoteModal,
        PreAcceptQuoteModal,
        PunchOutConnectionModal,
        PurchaseOrderModal,
        QuoteAcceptedModal,
        BillingInformationModal,
        PurchaseOrderProductTour,
    } from './dialogs';
    import {
        clientWorkRate,
        feesPerWorkUnit,
        isQuoteAcceptanceBlockedFor,
        isQuoteActionable,
        isQuoteFixedPrice,
        isQuoteTimeAndMaterials,
    } from '../../utils';
    import type {AllTranslationsRecord} from '#build/i18n';

    const {locale} = useLocale();
    const {formatDate} = useFormat();
    const {t} = useTranslation();
    const {track} = useDataTracking();

    const props = defineProps<{
        quote: QuoteResource;
        isFeesInfoVisible: boolean;
        showAddPhoneNumber: boolean;
    }>();

    useAmplitudeSessionReplay(() => !!props.quote.projectTerms);

    const clientQuoteStore = useClientQuoteViewStore();
    const {r$} = storeToRefs(clientQuoteStore);
    const isPaymentDetailsEnabled = useFeatureFlag('PROJ-3121-quote-validation-payment-details');

    const showPurchaseOrderTour = ref(false);
    const purchaseOrderTourDismissedThisVisit = ref(false);

    const userStore = useUserStore();

    const {resolvePurchaseOrderChange, trackPurchaseOrderAddedOrUpdated} = useTrackPurchaseOrder();

    watch(
        () => props.quote.id,
        () => {
            purchaseOrderTourDismissedThisVisit.value = false;
            showPurchaseOrderTour.value = false;
        },
    );

    const feesPerWorkUnitForClient = computed(() => feesPerWorkUnit(props.quote));
    const workRateForClient = computed(() => clientWorkRate(props.quote));
    const loading = ref(false);

    const acceptQuoteTooltipText = computed(() => {
        const reason = props.quote.options.acceptanceBlockingReason;
        const key = reason ? ACCEPTANCE_BLOCKING_TOOLTIP_KEYS[reason] : undefined;
        return key ? t(key) : undefined;
    });

    const ACCEPTANCE_BLOCKING_TOOLTIP_KEYS: Partial<Record<QuoteAcceptanceBlockingReason, keyof AllTranslationsRecord>> = {
        [QuoteAcceptanceBlockingReason.FreelancerVerification]:
            'projectclient.quote.view.quote-summary.actions.accept-quote.tooltip.acceptance-blocked',
        [QuoteAcceptanceBlockingReason.AcceptanceRoleRequired]:
            'projectclient.quote.view.quote-summary.actions.accept-quote.tooltip.acceptance.role.required',
    };

    // handle pre-accept + accept quote
    const projectId = ref<string | undefined>(undefined);
    const {openDialog: openAcceptedQuoteDialog} = useDialog(QuoteAcceptedModal, {
        props: {
            freelancerName: props.quote.freelancer.firstName,
            projectId,
        },
    });

    const {openDialog: openPhoneNumberDialog} = useDialog(AddPhoneNumberModal, {
        onConfirm() {
            acceptQuote({
                purchaseOrderCheck: false,
                phoneNumberCheck: false,
                billingInformationCheck: true,
            });
        },
    });

    const {openDialog: openPhoneNumberDialogForPreAccept} = useDialog(AddPhoneNumberModal, {
        onConfirm() {
            openPreAcceptQuoteDialog();
        },
    });

    type AcceptQuoteChecks = {
        purchaseOrderCheck: boolean;
        phoneNumberCheck: boolean;
        billingInformationCheck: boolean;
    };

    const {openDialog: openBillingInformationModal} = useDialog(BillingInformationModal, {
        async onConfirm() {
            await acceptQuote({
                purchaseOrderCheck: false,
                phoneNumberCheck: false,
                billingInformationCheck: false,
            });
        },
    });

    async function acceptRequirementCheck() {
        if (props.quote.options.acceptanceBlockingReason) {
            return;
        }
        if (props.quote.projectTerms) {
            const {valid} = await validateForm(r$);
            if (!valid) {
                track('client_quote_acceptance_attempted', {
                    fail_reason: 'payment T&C not accepted',
                    quote_id: props.quote.id,
                    invoicing_mode: toInvoicingMode(props.quote.invoicingMode),
                    client_payment_method: props.quote.options.isPrepayment ? 'pre-payment' : 'invoice before payment',
                });
                return;
            }
        }

        acceptQuote({
            purchaseOrderCheck: true,
            phoneNumberCheck: true,
            billingInformationCheck: true,
        });
    }

    async function acceptQuote(checks: AcceptQuoteChecks) {
        const needsPurchaseOrder = checks.purchaseOrderCheck && props.quote.options?.needClientPurchaseOrder && !props.quote.purchaseOrder;

        if (needsPurchaseOrder && !purchaseOrderTourDismissedThisVisit.value) {
            showPurchaseOrderTour.value = true;
            return;
        }

        if (checks.phoneNumberCheck && props.showAddPhoneNumber) {
            openPhoneNumberDialog();
            return;
        }

        if (checks.billingInformationCheck && props.quote.options.needClientBillingInformation) {
            openBillingInformationModal({
                companyId: props.quote.options.isOpenQuote ? userStore.companyId : props.quote.client.company.id,
                quoteId: props.quote.id,
            });
            return;
        }

        loading.value = true;
        try {
            const result = await clientQuoteStore.acceptQuote(props.quote.id);
            if (result) {
                projectId.value = result.projectId;
                openAcceptedQuoteDialog();
            }
        } finally {
            loading.value = false;
        }
    }

    async function onPurchaseOrderTourAddLater() {
        showPurchaseOrderTour.value = false;
        purchaseOrderTourDismissedThisVisit.value = true;
        await acceptQuote({
            purchaseOrderCheck: false,
            phoneNumberCheck: true,
            billingInformationCheck: true,
        });
    }

    function onPurchaseOrderTourAddNow() {
        showPurchaseOrderTour.value = false;
        activateSave();
        void nextTick(() => {
            purchaseOrderInputRef.value?.focus();
        });
    }

    const {openDialog: openPreAcceptQuoteDialog} = useDialog(PreAcceptQuoteModal, {
        props: {
            quoteId: props.quote.id,
            clientCompanyId: props.quote.client.company.id,
        },
    });

    async function preAcceptRequirementCheck() {
        if (props.quote.projectTerms) {
            const {valid} = await validateForm(r$);
            if (!valid) {
                return;
            }
        }

        if (props.showAddPhoneNumber) {
            openPhoneNumberDialogForPreAccept();
        } else {
            openPreAcceptQuoteDialog();
        }
    }

    // handle punchout flow
    const isPunchoutEnabled = computed(() => props.quote.options.punchout.enabled);
    const erpName = computed(() => props.quote.options.punchout.details?.procurementAppName);
    const erpUrl = computed(() => props.quote.options.punchout.details?.procurementAppUrl);
    const punchoutStatus = props.quote.options.punchout.details?.status;

    const {openDialog: openPunchOutModal} = useDialog(PunchOutConnectionModal, {
        props: {
            quoteId: props.quote.id,
            erpUrl,
            erpName,
        },
    });

    async function handlePunchout() {
        if (props.quote.options.punchout.details?.punchoutSessionActive) {
            await handleSendQuoteToErp();
        } else {
            openPunchOutModal();
        }
    }

    async function handleSendQuoteToErp() {
        const quoteId = props.quote.id;
        loading.value = true;
        const res = await clientQuoteStore.sendQuoteToErp(quoteId);
        loading.value = false;
        if (res && res.redirectionUrl != null) {
            window.location.href = res.redirectionUrl;
        }
    }

    // handle decline quote
    const {openDialog: openDeclineQuoteDialog} = useDialog(DeclineQuoteModal, {
        props: {
            quoteId: props.quote.id,
            freelancerName: props.quote.freelancer.firstName,
        },
    });

    // handle purchase order dialog
    const purchaseOrderInputRef = useTemplateRef('purchaseOrderInputRef');

    const {openDialog: openPurchaseOrderDialog} = useDialog(PurchaseOrderModal, {
        props: {
            quoteId: props.quote.id,
            purchaseOrderInputRef: purchaseOrderInputRef as unknown as Ref<HTMLElement>,
        },
    });

    // handle purchase order input
    const purchaseOrder = ref(props.quote.purchaseOrder);
    const isInputDisabled = ref(true);

    const isRegexFaulty = ref(false);
    const regex = /^(?=.*\d)[\w-]{1,25}$/;

    watch(
        () => purchaseOrder.value,
        () => {
            if (purchaseOrder.value) {
                isRegexFaulty.value = !regex.test(purchaseOrder.value.trim());
            }
        },
    );

    function activateSave() {
        if (!isQuoteActionable(props.quote)) return;
        isInputDisabled.value = false;
    }

    async function handleSetPurchaseOrder() {
        if (purchaseOrder.value && isQuoteActionable(props.quote)) {
            const purchaseOrderChange = resolvePurchaseOrderChange(props.quote.purchaseOrder, purchaseOrder.value);
            const result = await clientQuoteStore.setPurchaseOrder({
                quoteId: props.quote.id,
                purchaseOrderParam: {
                    purchaseOrderNumber: purchaseOrder.value.trim() ?? '',
                },
            });
            if (result === 'applied') {
                await trackPurchaseOrderAddedOrUpdated(purchaseOrderChange, 'quote_acceptance_page', props.quote.options.needClientPurchaseOrder);
            }
        }
        isInputDisabled.value = true;
        await clientQuoteStore.getQuote(props.quote.id);
    }

    function toInvoicingMode(invoicingMode: InvoicingMode): 'Direct' | 'UP Standard' | 'UP Custom (MSA)' {
        switch (invoicingMode) {
            case InvoicingMode.Direct:
                return 'Direct';
            case InvoicingMode.Tripartite:
                return 'UP Standard';
            case InvoicingMode.Subcontracting:
                return 'UP Custom (MSA)';
        }
    }

    function displayFreelancerMessengerDrawer() {
        eventBus.emit('showFreelancerDrawer', {tab: FreelancerDrawerTab.Messages});
    }

    useEventBus(eventBus, 'handleAddPurchaseOrderLater', async () => {
        await acceptQuote({
            purchaseOrderCheck: false,
            phoneNumberCheck: true,
            billingInformationCheck: true,
        });
    });

    useEventBus(eventBus, 'handleAddPurchaseOrderNow', () => {
        purchaseOrderInputRef.value?.focus();
    });
</script>

<style lang="scss" scoped>
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/text' as text;

    :deep(.background.joy-highlight) {
        background: var(--joy-color-secondary-10);
    }

    .quote-summary {
        &__po-section {
            display: flex;
            width: 100%;
            gap: var(--joy-core-spacing-2);
            margin: var(--joy-core-spacing-2) 0;

            :deep(.joy-input--wrapper) {
                margin-bottom: 0;
            }
        }

        hr {
            border: 1px solid var(--joy-color-neutral-30);
            margin: var(--joy-core-spacing-4) 0 var(--joy-core-spacing-0);
        }

        &__amounts {
            display: flex;
            flex-direction: column;
            gap: var(--joy-core-spacing-4);
        }

        &__price-details {
            &__header {
                @include text.getFontBySize(primary-450);

                line-height: var(--joy-line-height-large);
            }

            &__wrapper {
                display: flex;
                justify-content: space-between;
                align-items: center;

                @include text.getFontBySize(primary-300);

                p {
                    margin-bottom: 0;
                    white-space: nowrap;
                }

                &__tooltip-label {
                    width: 70%;
                    display: flex;
                    align-items: center;
                    gap: var(--joy-core-spacing-1);
                }
            }
        }

        .joy-highlight {
            padding: var(--joy-core-spacing-2);
        }

        .bold {
            font-weight: var(--joy-font-weight-bold);
        }

        .teal {
            color: var(--joy-color-secondary-50);
        }

        .small {
            @include text.getFontBySize(primary-200);

            color: var(--joy-color-text-default);
        }

        &__cta-group,
        &__quote-actions {
            display: flex;
            flex-direction: column;
            align-items: stretch;
            gap: var(--joy-core-spacing-2);
        }

        &__cta-group {
            .quote-summary__accept-quote-tooltip {
                width: 100%;

                :deep(.joy-tooltip__wrapper) {
                    width: 100%;
                }

                &__trigger {
                    display: block;
                }
            }
        }

        &__quote-actions {
            margin-top: var(--joy-core-spacing-6);

            & > * {
                width: 100%;
            }

            &__highlight {
                p {
                    @include text.getFontBySize(primary-400);

                    color: var(--joy-color-text-default);
                }
            }
        }
    }
</style>
